import Daily, { type DailyCall } from "@daily-co/daily-js";
import { useCallback, useEffect, useRef, useState } from "react";

import { endConversation, startConversation, websocketUrl } from "../lib/api";
import type {
  ConversationState,
  ServerEvent,
  SourceGroup,
  TranscriptEntry,
} from "../lib/types";

/**
 * Owns the whole live conversation: the realtime room, the backend socket, and
 * the derived UI state.
 *
 * Two things worth knowing about this file.
 *
 * **The backend is the source of truth for the UI.** Status, transcript, and
 * sources all arrive over the WebSocket, because the backend is the thing that
 * actually knows what is happening — it receives the turn, decides to search,
 * and produces the reply. Reconstructing that in the browser from room events
 * would always be a guess one step behind.
 *
 * **Barge-in is signalled from here.** The browser is already holding the
 * realtime connection, so it sees the platform's interruption events before any
 * server-side webhook would. Forwarding them over the open socket saves a round
 * trip on the single most visible quality signal in the demo — and the local
 * audio-level watchdog below catches the case where the person starts talking
 * before the platform has decided that they have.
 */

const INTERRUPT_AUDIO_LEVEL = 0.06;
const INTERRUPT_COOLDOWN_MS = 900;

/**
 * Virtual audio devices — BlackHole, Loom, Zoom, Teams, OBS and friends —
 * enumerate as ordinary microphones and hand back a track that is valid,
 * `playable`, and completely silent unless something is routed into them.
 *
 * This matters because the browser keeps its own per-site input choice,
 * independent of the operating system's default device. macOS can insist the
 * built-in microphone is selected while the tab is quietly recording sixty-four
 * channels of nothing. The platform then hears silence, never detects a turn,
 * and the avatar waits forever — with no error anywhere, because nothing has
 * actually failed.
 */
const VIRTUAL_MIC =
  /blackhole|soundflower|loopback|virtual|aggregate|multi-output|zoom|teams|loom|obs|vb-audio|voicemeeter|krisp/i;

/** How long to let a silent microphone prove itself before switching away. */
const SILENT_MIC_GRACE_MS = 4000;

async function preferPhysicalMicrophone(call: DailyCall): Promise<string | null> {
  try {
    const { devices } = await call.enumerateDevices();
    const mics = devices.filter(
      (device) => device.kind === "audioinput" && device.deviceId,
    );
    if (!mics.length) return null;

    const current = await call.getInputDevices();
    const currentLabel = (current?.mic as MediaDeviceInfo | undefined)?.label ?? "";
    if (currentLabel && !VIRTUAL_MIC.test(currentLabel)) return null;

    const physical = mics.find(
      (device) => device.label && !VIRTUAL_MIC.test(device.label),
    );
    if (!physical) return null;

    await call.setInputDevicesAsync({ audioDeviceId: physical.deviceId });
    console.warn(
      `[adunni] input was the virtual device "${currentLabel || "unknown"}" — ` +
        `switched to "${physical.label}"`,
    );
    return physical.label;
  } catch (error) {
    console.error("[adunni] could not inspect audio input devices", error);
    return null;
  }
}

interface ConversationApi {
  status: ConversationState;
  connected: boolean;
  starting: boolean;
  error: string | null;
  transcript: TranscriptEntry[];
  sourceGroups: SourceGroup[];
  micOn: boolean;
  camOn: boolean;
  callObject: DailyCall | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  restart: () => Promise<void>;
  toggleMic: () => void;
  toggleCam: () => void;
}

export function useConversation(): ConversationApi {
  const [status, setStatus] = useState<ConversationState>("idle");
  const [connected, setConnected] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [sourceGroups, setSourceGroups] = useState<SourceGroup[]>([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [callObject, setCallObject] = useState<DailyCall | null>(null);

  const sessionIdRef = useRef<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const callRef = useRef<DailyCall | null>(null);
  const statusRef = useRef<ConversationState>("idle");
  const lastInterruptRef = useRef(0);
  const peakAudioRef = useRef(0);
  const lastLevelLogRef = useRef(0);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const send = useCallback((message: unknown) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  }, []);

  const requestInterrupt = useCallback(() => {
    const now = Date.now();
    if (now - lastInterruptRef.current < INTERRUPT_COOLDOWN_MS) return;
    lastInterruptRef.current = now;
    send({ type: "interrupt" });
  }, [send]);

  // ── Server events ─────────────────────────────────────────────────────────

  const handleServerEvent = useCallback((event: ServerEvent) => {
    switch (event.type) {
      case "status":
        setStatus(event.state);
        break;
      case "interaction":
        // Tavus takes interactions over the WebRTC data channel, not HTTP, and
        // we are the only participant in the room — so the backend relays
        // through here.
        if (!callRef.current) {
          // Audio for a turn that has nowhere to go. Without this the room is
          // simply silent and nothing anywhere says why.
          console.error("[adunni] no call object — dropped", event.payload);
          break;
        }
        try {
          callRef.current.sendAppMessage(event.payload, "*");
        } catch (error) {
          // Daily rejects app messages over 4kB. The backend frames audio to
          // stay under that, so this firing means the limit moved or a
          // non-audio interaction grew.
          console.error("[adunni] sendAppMessage failed", error, {
            eventType: (event.payload as { event_type?: string }).event_type,
            bytes: JSON.stringify(event.payload).length,
          });
        }
        break;
      case "transcript":
        setTranscript((entries) => [
          ...entries,
          {
            id: `${Date.now()}-${entries.length}`,
            role: event.role,
            text: event.text,
            interrupted: event.interrupted,
          },
        ]);
        break;
      case "sources":
        if (event.sources.length) {
          setSourceGroups((groups) => [
            { id: `${Date.now()}`, query: event.query, sources: event.sources },
            ...groups,
          ].slice(0, 4));
        }
        break;
      case "error":
        setError(event.message);
        break;
      case "ended":
        setStatus("idle");
        break;
      default:
        break;
    }
  }, []);

  // ── Teardown ──────────────────────────────────────────────────────────────

  const teardown = useCallback(async () => {
    socketRef.current?.close();
    socketRef.current = null;

    const call = callRef.current;
    callRef.current = null;
    setCallObject(null);
    if (call) {
      try {
        await call.leave();
      } catch {
        /* already gone */
      }
      call.destroy();
    }

    setConnected(false);
    setStatus("idle");
  }, []);

  // ── Start ─────────────────────────────────────────────────────────────────

  const start = useCallback(async () => {
    if (starting || callRef.current) return;

    setStarting(true);
    setError(null);
    setTranscript([]);
    setSourceGroups([]);

    try {
      const session = await startConversation();
      sessionIdRef.current = session.session_id;

      // Socket first, so no early status or transcript event is missed.
      const socket = new WebSocket(websocketUrl(session.session_id));
      socketRef.current = socket;
      socket.onmessage = (message) => {
        try {
          handleServerEvent(JSON.parse(message.data) as ServerEvent);
        } catch {
          /* ignore malformed frames */
        }
      };
      socket.onerror = () => setError("Lost the connection to the backend.");

      const call = Daily.createCallObject({ subscribeToTracksAutomatically: true });
      callRef.current = call;
      setCallObject(call);

      // Everything the platform emits is forwarded; the backend decides which
      // events mean "she has been interrupted".
      call.on("app-message", (event) => {
        // Logged, not just forwarded. This channel carries the platform's own
        // pipeline events — speech recognition, utterances, turn boundaries —
        // and it is the only place they are observable. Seeing nothing here
        // while you talk means the platform is not transcribing you at all.
        console.debug("[adunni] platform event", event?.data);
        if (event?.data) send({ type: "tavus_event", event: event.data });
      });
      call.on("left-meeting", () => {
        setConnected(false);
        setStatus("idle");
      });

      // The failure this catches is silent by construction: the room stays up,
      // her video keeps rendering, and only the *outbound* transport is gone —
      // so she never hears another word and just sits there. Surface it rather
      // than letting it look like she has nothing to say.
      call.on("network-connection", (event) => {
        console.info("[adunni] network-connection", event?.type, event?.event);
        if (event?.event === "interrupted") {
          setError("Connection to the room dropped — she cannot hear you. Restarting…");
        } else if (event?.event === "connected") {
          setError(null);
        }
      });
      call.on("error", (event) => {
        setError(event?.errorMsg ?? "The video connection failed.");
        setStatus("error");
      });

      // Device failures do NOT fail the join. Without this the call looks
      // healthy, the greeting plays, and the microphone is simply never
      // published — so the platform hears nothing, never transcribes a turn,
      // and never calls our LLM endpoint. Silence, with the UI insisting the
      // mic is on.
      call.on("camera-error", (event) => {
        const detail =
          event?.errorMsg?.errorMsg ?? event?.error?.msg ?? "microphone or camera blocked";
        console.error("[adunni] device error", event);
        setError(`Microphone unavailable — she cannot hear you. ${detail}`);
      });

      // Local watchdog: if the person starts talking while she is speaking,
      // cut her off immediately rather than waiting for the platform to notice.
      call.on("local-audio-level", (event) => {
        // Daily emits this as a *string* ("0.000334"), not a number. Coerce
        // before any arithmetic — calling a number method on it throws inside
        // the emitter, and Daily swallows that as "could not emit", taking the
        // barge-in watchdog below down with it.
        const level = Number(event?.audioLevel ?? 0) || 0;

        // A track can be "playable" and still carry pure silence — wrong input
        // device, muted at the OS, or a virtual device with nothing behind it.
        // The platform then hears nothing, never raises a turn, and the avatar
        // just waits. This is the only place that distinguishes "she is
        // ignoring you" from "no sound is leaving this machine".
        peakAudioRef.current = Math.max(peakAudioRef.current, level);
        if (Date.now() - lastLevelLogRef.current > 2000) {
          lastLevelLogRef.current = Date.now();
          console.info(
            `[adunni] mic level now=${level.toFixed(3)} peak=${peakAudioRef.current.toFixed(3)}`,
            peakAudioRef.current < 0.01
              ? "← SILENT. She cannot hear you; check the input device."
              : "",
          );
        }

        if (statusRef.current === "speaking" && level > INTERRUPT_AUDIO_LEVEL) {
          requestInterrupt();
        }
      });

      await call.join({ url: session.conversation_url });

      // The backend cannot speak until this moment. When she greets in her own
      // voice the audio is relayed through *this* tab's data channel, so the
      // greeting has to wait for the room to exist — pushed any earlier it goes
      // nowhere and the call opens on silence. Harmless when the platform is
      // doing the greeting: the backend ignores it.
      send({ type: "ready" });

      call.startLocalAudioLevelObserver(100);
      await preferPhysicalMicrophone(call);

      // The label check above catches the common case. This catches the rest:
      // a device that does not look virtual but still delivers nothing. Judge
      // it on the only evidence that actually matters — whether any sound has
      // arrived — and say so plainly instead of leaving her mutely waiting.
      window.setTimeout(() => {
        if (!callRef.current || peakAudioRef.current > 0.01) return;
        void preferPhysicalMicrophone(callRef.current).then((switched) => {
          if (switched) return;
          setError(
            "No sound is reaching the call — she cannot hear you. Check which " +
              "microphone this tab is using (click the icon in the address bar); " +
              "a virtual device such as BlackHole or Zoom records silence.",
          );
        });
      }, SILENT_MIC_GRACE_MS);

      // Report what the microphone is actually doing rather than assuming it
      // came up. A call with no outbound audio is indistinguishable from a
      // working one until you notice she never answers.
      const local = call.participants()?.local;
      const audioState = local?.tracks?.audio?.state;
      const micLive = audioState === "playable" || audioState === "sendable";
      console.info("[adunni] joined — local audio track:", audioState);
      if (!micLive) {
        setError(
          `Microphone is not publishing (track state: ${audioState ?? "unknown"}). ` +
            "She will not hear you — check the browser's microphone permission.",
        );
      }

      setMicOn(micLive);
      setCamOn(local?.tracks?.video?.state === "playable");
      setConnected(true);
      setStatus("listening");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start the call.");
      setStatus("error");
      await teardown();
    } finally {
      setStarting(false);
    }
  }, [handleServerEvent, requestInterrupt, send, starting, teardown]);

  // ── Stop / restart ────────────────────────────────────────────────────────

  const stop = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    await teardown();
    if (sessionId) {
      try {
        await endConversation(sessionId);
      } catch {
        /* the call also expires on its own */
      }
    }
  }, [teardown]);

  const restart = useCallback(async () => {
    await stop();
    await start();
  }, [start, stop]);

  // ── Device controls ───────────────────────────────────────────────────────

  const toggleMic = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !call.localAudio();
    call.setLocalAudio(next);
    setMicOn(next);
  }, []);

  const toggleCam = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !call.localVideo();
    call.setLocalVideo(next);
    setCamOn(next);
  }, []);

  useEffect(() => () => void teardown(), [teardown]);

  return {
    status,
    connected,
    starting,
    error,
    transcript,
    sourceGroups,
    micOn,
    camOn,
    callObject,
    start,
    stop,
    restart,
    toggleMic,
    toggleCam,
  };
}
